import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import LabViewer from '../Viewer/LabViewer';
import XlabsStageChrome from '../Xlabs/XlabsStageChrome';
import SimLabToolRail from '../Xlabs/SimLabToolRail';
import SimLabSidePanel from '../Xlabs/SimLabSidePanel';
import LabAnnotateConsole from '../Xlabs/LabAnnotateConsole';
import XlabsThemeViewPanel from '../Xlabs/XlabsThemeViewPanel';
import {
  allCatalogEntries,
  catalogGlbUrl,
  catalogLabel,
  DEFAULT_SITE_PLANNER_MODEL,
} from '../../data/sitePlannerCatalog';
import { preloadCatalogModel } from '../../services/catalogModelPreload';
import {
  DEFAULT_FEATURE_VISIBILITY,
} from '../../services/modelFeatureMap';
import {
  buildInOutTraverse,
  playTraverseStops,
} from '../../services/featureTraverse';
import {
  ANNOTATION_KINDS,
  loadAnnotations,
  saveAnnotations,
  syncClearAnnotations,
  syncCreateAnnotation,
  syncDeleteAnnotation,
  syncLoadAnnotations,
  syncPatchAnnotation,
} from '../../services/labAnnotations';
import { R1AS_FAUCET_STATION_IDS } from '../../data/r1asFaucets';
import {
  defaultAmbientPresetForExterior,
  getAmbientPreset,
} from '../../data/labAmbientPresets';
import {
  ambientApplyTracks,
  ambientDispose,
  ambientSetEnabled,
  ambientSetMasterVolume,
  ambientSetTrackVolume,
} from '../../services/labAmbientAudio';
import { useAuth } from '../Auth/AuthGate';
import useInternalTheme from '../../hooks/useInternalTheme';
import './XlabsLocate.css';
import './SimulationLab.css';

/**
 * Simulation Lab — docs/SIMULATION_LAB.md · docs/MODEL_FEATURE_MAP.md
 * · docs/SIM_LAB_WALK_CAMERA.md · docs/SIM_LAB_ANNOTATE.md
 * · docs/SIM_LAB_EXTERIOR_ENV.md
 */

const RAIL_PRESETS = new Set(['iso', 'front', 'side', 'top']);

export default function SimulationLab() {
  const theme = useInternalTheme();
  const { email: authEmail } = useAuth();
  const [slug, setSlug] = useState(DEFAULT_SITE_PLANNER_MODEL);
  const [activeUrl, setActiveUrl] = useState(
    catalogGlbUrl(DEFAULT_SITE_PLANNER_MODEL)
  );
  const [activeSlug, setActiveSlug] = useState(DEFAULT_SITE_PLANNER_MODEL);
  const [layers, setLayers] = useState({
    roof: true,
    walls: true,
    interior: true,
  });
  /** Horizontal section cut — docs/SIM_LAB_SECTION_CUT.md */
  const [sectionCut, setSectionCut] = useState({ enabled: false, t: 0.85 });
  /** Exterior place — docs/SIM_LAB_EXTERIOR_ENV.md */
  const [exteriorId, setExteriorId] = useState('none');
  /** Sky variant asset id (null = env default). */
  const [exteriorSkyId, setExteriorSkyId] = useState(null);
  const [exteriorLayers, setExteriorLayers] = useState({
    sky: true,
    ground: true,
  });

  /** Ambient audio — docs/SIM_LAB_AMBIENT_AUDIO.md */
  const [ambientOn, setAmbientOn] = useState(false);
  const [ambientPreset, setAmbientPreset] = useState('off');
  const [ambientMaster, setAmbientMaster] = useState(80);
  const [ambientTracks, setAmbientTracks] = useState({
    surf: 0,
    wind: 0,
    gulls: 0,
  });

  const applyAmbientPreset = useCallback((presetId) => {
    const preset = getAmbientPreset(presetId);
    setAmbientPreset(preset.id);
    const next = {
      surf: Math.round((preset.tracks.surf || 0) * 100),
      wind: Math.round((preset.tracks.wind || 0) * 100),
      gulls: Math.round((preset.tracks.gulls || 0) * 100),
    };
    setAmbientTracks(next);
    ambientApplyTracks(preset.tracks);
  }, []);

  const onAmbientToggle = useCallback(() => {
    setAmbientOn((on) => {
      ambientSetEnabled(!on);
      return !on;
    });
  }, []);

  const onAmbientMaster = useCallback((v) => {
    setAmbientMaster(v);
    ambientSetMasterVolume(v / 100);
  }, []);

  const onAmbientTrack = useCallback((id, v) => {
    setAmbientTracks((prev) => ({ ...prev, [id]: v }));
    ambientSetTrackVolume(id, v / 100);
    setAmbientPreset('custom');
  }, []);

  useEffect(() => {
    ambientSetMasterVolume(ambientMaster / 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => ambientDispose(), []);

  const onExteriorIdChange = useCallback(
    (id) => {
      setExteriorId(id);
      setExteriorSkyId(null);
      applyAmbientPreset(defaultAmbientPresetForExterior(id));
    },
    [applyAmbientPreset]
  );
  const [showLabels, setShowLabels] = useState(true);
  const [featureMap, setFeatureMap] = useState(null);
  const [featureVisibility, setFeatureVisibility] = useState(
    DEFAULT_FEATURE_VISIBILITY
  );
  const [highlightDoors, setHighlightDoors] = useState(false);
  const [traversePlaying, setTraversePlaying] = useState(false);
  const [traverseStopLabel, setTraverseStopLabel] = useState('');
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [shotUrl, setShotUrl] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [activePreset, setActivePreset] = useState('iso');
  const [walkMode, setWalkMode] = useState(false);
  const [annotateMode, setAnnotateMode] = useState(false);
  const [annotations, setAnnotations] = useState(() =>
    loadAnnotations(DEFAULT_SITE_PLANNER_MODEL)
  );
  const [annotateKind, setAnnotateKind] = useState('faucet-hit');
  const [annotateStationId, setAnnotateStationId] = useState('sink-vanity');
  const [annotateSelectedId, setAnnotateSelectedId] = useState(null);
  const [annotateLogs, setAnnotateLogs] = useState([]);
  const [annotateSource, setAnnotateSource] = useState('local');
  const [loadGen, setLoadGen] = useState(0);
  const apiRef = useRef(null);
  const shotObjectUrl = useRef(null);
  const traverseSignal = useRef({ cancelled: false });

  const catalog = useMemo(() => allCatalogEntries(), []);
  const stationIds = useMemo(() => R1AS_FAUCET_STATION_IDS, []);

  const onBindApi = useCallback((api) => {
    apiRef.current = api;
  }, []);

  const exitWalk = useCallback(() => {
    if (annotateMode) {
      setAnnotateMode(false);
      setStatus('Annotate off');
      return;
    }
    setWalkMode(false);
    setStatus('Orbit restored');
  }, [annotateMode]);

  const toggleWalk = useCallback(() => {
    setWalkMode((on) => {
      if (on) {
        setAnnotateMode(false);
        setStatus('Orbit restored');
        return false;
      }
      traverseSignal.current.cancelled = true;
      setTraversePlaying(false);
      setTraverseStopLabel('');
      setStatus('Walk · 6 ft POV · WASD · N annotate · Esc exit');
      return true;
    });
  }, []);

  const pushAnnLog = useCallback((msg) => {
    const t = new Date().toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    setAnnotateLogs((prev) => [...prev.slice(-12), { t, msg }]);
  }, []);

  const toggleAnnotate = useCallback(() => {
    setAnnotateMode((on) => {
      const next = !on;
      if (next) {
        pushAnnLog(`on · ${annotateKind} @ ${annotateStationId}`);
        setStatus('Annotate on');
      } else {
        pushAnnLog('off');
        setStatus('Annotate off');
      }
      return next;
    });
  }, [annotateKind, annotateStationId, pushAnnLog]);

  const onAnnotationAdd = useCallback(
    async (pin) => {
      const withAuthor = {
        ...pin,
        notes: pin.notes || '',
        userEmail: authEmail || pin.userEmail || '',
      };
      setAnnotations((prev) => {
        const next = [...prev, withAuthor];
        saveAnnotations(activeSlug, next);
        return next;
      });
      setAnnotateSelectedId(withAuthor.id);
      const short = withAuthor.kind.replace('faucet-', '');
      pushAnnLog(`+ ${short} @ ${withAuthor.stationId}`);
      setStatus(`Pinned · ${withAuthor.kind}`);

      const synced = await syncCreateAnnotation(withAuthor);
      setAnnotateSource(synced.source);
      if (synced.ok && synced.annotation) {
        setAnnotations((prev) => {
          const next = prev.map((a) =>
            a.id === synced.annotation.id ? synced.annotation : a
          );
          saveAnnotations(activeSlug, next);
          return next;
        });
        pushAnnLog(`sync neon · ${short}`);
      } else {
        pushAnnLog('sync local only');
      }
    },
    [activeSlug, authEmail, pushAnnLog]
  );

  const onAnnotationDelete = useCallback(
    async (id) => {
      setAnnotations((prev) => {
        const gone = prev.find((a) => a.id === id);
        const next = prev.filter((a) => a.id !== id);
        saveAnnotations(activeSlug, next);
        if (gone) {
          pushAnnLog(`− ${gone.kind.replace('faucet-', '')} @ ${gone.stationId}`);
        }
        return next;
      });
      setAnnotateSelectedId((cur) => (cur === id ? null : cur));
      setStatus('Pin deleted');
      const synced = await syncDeleteAnnotation(id);
      setAnnotateSource(synced.source);
      if (!synced.ok) pushAnnLog('delete cache only');
    },
    [activeSlug, pushAnnLog]
  );

  const onAnnotationClear = useCallback(async () => {
    setAnnotations([]);
    setAnnotateSelectedId(null);
    pushAnnLog('cleared');
    setStatus('Pins cleared for slug');
    const synced = await syncClearAnnotations(activeSlug);
    setAnnotateSource(synced.source);
  }, [activeSlug, pushAnnLog]);

  const onAnnotationNotes = useCallback(
    async (id, notes) => {
      setAnnotations((prev) => {
        const next = prev.map((a) =>
          a.id === id ? { ...a, notes, updatedAt: new Date().toISOString() } : a
        );
        saveAnnotations(activeSlug, next);
        return next;
      });
      const synced = await syncPatchAnnotation(id, { notes });
      setAnnotateSource(synced.source);
      if (synced.ok && synced.annotation) {
        setAnnotations((prev) => {
          const next = prev.map((a) =>
            a.id === synced.annotation.id ? synced.annotation : a
          );
          saveAnnotations(activeSlug, next);
          return next;
        });
        pushAnnLog('notes saved · neon');
      } else {
        pushAnnLog('notes saved · local');
      }
      setStatus('Notes saved');
    },
    [activeSlug, pushAnnLog]
  );

  const onAnnotationExport = useCallback(async () => {
    const text = JSON.stringify(annotations, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      pushAnnLog(`export ${annotations.length} → clipboard`);
      setStatus(`Exported ${annotations.length} pins`);
    } catch {
      pushAnnLog('export failed');
      setStatus('Export failed — see browser console');
      // eslint-disable-next-line no-console
      console.log(text);
    }
  }, [annotations, pushAnnLog]);

  const setAnnotateKindLogged = useCallback(
    (kind) => {
      setAnnotateKind(kind);
      if (annotateMode) pushAnnLog(`target · ${kind} @ ${annotateStationId}`);
    },
    [annotateMode, annotateStationId, pushAnnLog]
  );

  const setAnnotateStationLogged = useCallback(
    (id) => {
      setAnnotateStationId(id);
      if (annotateMode) pushAnnLog(`target · ${annotateKind} @ ${id}`);
    },
    [annotateMode, annotateKind, pushAnnLog]
  );

  const loadModel = useCallback(() => {
    traverseSignal.current.cancelled = true;
    setTraversePlaying(false);
    setTraverseStopLabel('');
    setWalkMode(false);
    setAnnotateMode(false);
    const url = catalogGlbUrl(slug);
    setLoading(true);
    setStatus(`Loading ${catalogLabel(slug)}…`);
    setFeatureMap(null);
    setFeatureVisibility({ ...DEFAULT_FEATURE_VISIBILITY });
    setHighlightDoors(false);
    setShotUrl((prev) => {
      if (shotObjectUrl.current) {
        URL.revokeObjectURL(shotObjectUrl.current);
        shotObjectUrl.current = null;
      }
      return null;
    });
    preloadCatalogModel(slug);
    setActiveSlug(slug);
    setActiveUrl(url);
    setActivePreset('iso');
    setAnnotations(loadAnnotations(slug));
    setAnnotateSelectedId(null);
    setAnnotateSource('local');
    // Remount LabViewer even when reloading the same URL (otherwise loading sticks).
    setLoadGen((n) => n + 1);
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { annotations: list, source } = await syncLoadAnnotations(activeSlug);
      if (cancelled) return;
      setAnnotations(list);
      setAnnotateSource(source);
      if (source === 'neon') {
        pushAnnLog(`loaded ${list.length} · neon`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSlug, authEmail, pushAnnLog]);

  useEffect(() => {
    preloadCatalogModel(DEFAULT_SITE_PLANNER_MODEL);
  }, []);

  useEffect(() => {
    if (stats?.slug === activeSlug) {
      setLoading(false);
      setStatus(`Ready · ${catalogLabel(activeSlug)}`);
    }
  }, [stats, activeSlug]);

  useEffect(
    () => () => {
      if (shotObjectUrl.current) URL.revokeObjectURL(shotObjectUrl.current);
    },
    []
  );

  const goPreset = useCallback((key) => {
    setWalkMode(false);
    setActivePreset(key);
    apiRef.current?.goToPreset?.(key, true);
  }, []);

  const capture = useCallback(async () => {
    const api = apiRef.current;
    if (!api?.capturePng) {
      setStatus('Viewer not ready for capture');
      return;
    }
    setCapturing(true);
    setStatus('Capturing…');
    try {
      await api.waitSettled?.();
      const blob = await api.capturePng();
      if (shotObjectUrl.current) URL.revokeObjectURL(shotObjectUrl.current);
      const url = URL.createObjectURL(blob);
      shotObjectUrl.current = url;
      setShotUrl(url);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeSlug}-lab.png`;
      a.click();
      setStatus(`Screenshot saved · ${activeSlug}-lab.png`);
    } catch (err) {
      setStatus(err?.message || 'Capture failed');
    } finally {
      setCapturing(false);
    }
  }, [activeSlug]);

  useEffect(() => {
    const onKey = (e) => {
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.tagName === 'SELECT'
      ) {
        return;
      }
      const k = e.key.toLowerCase();
      if (k === 'g') {
        e.preventDefault();
        toggleWalk();
        return;
      }
      if (k === 'n') {
        e.preventDefault();
        toggleAnnotate();
        return;
      }
      if (k === 'escape' && annotateMode) {
        e.preventDefault();
        setAnnotateMode(false);
        setStatus('Annotate off');
        return;
      }
      // Preset letters ignored while walking / annotating
      if (walkMode || annotateMode) {
        if (k === 'c' && !annotateMode) {
          e.preventDefault();
          capture();
        }
        return;
      }
      if (k === 'i') goPreset('iso');
      else if (k === 'f') goPreset('front');
      else if (k === 's') goPreset('side');
      else if (k === 't') goPreset('top');
      else if (k === 'c') {
        e.preventDefault();
        capture();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPreset, capture, toggleWalk, toggleAnnotate, walkMode, annotateMode]);

  const toggleLayer = (key) => {
    setLayers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleFeature = useCallback((kind) => {
    setFeatureVisibility((prev) => ({
      ...prev,
      [kind]: prev[kind] === false,
    }));
  }, []);

  const removeDoors = useCallback(() => {
    setFeatureVisibility((prev) => ({ ...prev, door: false }));
    setStatus('Doors hidden (feature map)');
  }, []);

  const restoreDoors = useCallback(() => {
    setFeatureVisibility((prev) => ({ ...prev, door: true }));
    setStatus('Doors restored');
  }, []);

  const stopTraverse = useCallback(() => {
    traverseSignal.current.cancelled = true;
    setTraversePlaying(false);
    setTraverseStopLabel('');
    setStatus('Traverse stopped');
  }, []);

  const playTraverse = useCallback(async () => {
    const api = apiRef.current;
    const localize = api?.getLocalize?.();
    if (!api?.setLookAt || !localize) {
      setStatus('Viewer not ready for traverse');
      return;
    }
    const stops = buildInOutTraverse(localize);
    if (!stops.length) {
      setStatus('No traverse stops');
      return;
    }
    setWalkMode(false);
    traverseSignal.current = { cancelled: false };
    setTraversePlaying(true);
    setStatus('Traverse · in → out');
    try {
      await playTraverseStops(api, stops, {
        signal: traverseSignal.current,
        onStop: (stop) => {
          setTraverseStopLabel(stop.label);
          setActivePreset(stop.id);
          setStatus(`Traverse · ${stop.label}`);
        },
        onFeatureVisibility: (vis) => setFeatureVisibility({ ...vis }),
        onLayers: (next) => setLayers({ ...next }),
      });
      if (!traverseSignal.current.cancelled) {
        setStatus('Traverse complete');
        setActivePreset('iso');
      }
    } catch (err) {
      setStatus(err?.message || 'Traverse failed');
    } finally {
      setTraversePlaying(false);
      setTraverseStopLabel('');
    }
  }, []);

  useEffect(
    () => () => {
      traverseSignal.current.cancelled = true;
    },
    []
  );

  const hudLeft = [
    { k: 'model', v: catalogLabel(activeSlug) },
    { k: 'cam', v: walkMode ? 'walk 6ft' : activePreset },
  ];
  const hudRight = [
    {
      k: 'front',
      v: stats?.frontAxis
        ? `${stats.frontAxis}${stats.frontSource === 'doors' ? '·door' : ''}`
        : '—',
    },
    {
      k: 'cut',
      v: sectionCut.enabled
        ? `${Math.round((Number(sectionCut.t) || 0) * 100)}%`
        : 'off',
    },
    {
      k: 'ext',
      v: exteriorId && exteriorId !== 'none' ? exteriorId : 'lab',
    },
    {
      k: 'doors',
      v: featureMap ? String(featureMap.counts?.door ?? 0) : '—',
    },
  ];

  const railPreset =
    RAIL_PRESETS.has(activePreset) ? activePreset : 'iso';

  return (
    <div
      className="xlabs-stage"
      data-hub-theme={theme === 'light' ? undefined : theme}
    >
      <Helmet>
        <title>xlabs · sim lab</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <XlabsThemeViewPanel />

      <XlabsStageChrome
        activeStage="sim-lab"
        brandProj="Sim lab"
        brandTo="/internal"
        brandBack="← internal"
        routes={null}
        tool={walkMode ? 'walk' : activePreset}
        showNorthBadge={false}
        showAddressSearch={false}
        statusOverride={
          annotateMode
            ? 'annotate'
            : walkMode
              ? 'walk · 6 ft'
              : stats
                ? 'model ready'
                : 'idle'
        }
        modelLoadLabel={loading ? catalogLabel(activeSlug) : null}
        statusHint="Lab · Walk G · Annotate N · I/F/S/T · C"
        panelToggleLabel="Lab"
        autoOpenPanel
        compactPanelCta="Lab · Models"
        hudLeft={hudLeft}
        hudRight={hudRight}
        ToolRail={
          <SimLabToolRail
            activePreset={railPreset}
            onPreset={goPreset}
            onCapture={capture}
            capturing={capturing}
            walkMode={walkMode}
            onToggleWalk={toggleWalk}
            annotateMode={annotateMode}
            onToggleAnnotate={toggleAnnotate}
          />
        }
        SidePanel={
          <SimLabSidePanel
            catalog={catalog}
            slug={slug}
            onSlugChange={setSlug}
            onLoad={loadModel}
            layers={layers}
            onToggleLayer={toggleLayer}
            sectionCut={sectionCut}
            onSectionCutChange={setSectionCut}
            exteriorId={exteriorId}
            onExteriorIdChange={onExteriorIdChange}
            exteriorSkyId={exteriorSkyId}
            onExteriorSkyIdChange={setExteriorSkyId}
            exteriorLayers={exteriorLayers}
            onExteriorLayersChange={setExteriorLayers}
            ambientOn={ambientOn}
            onAmbientToggle={onAmbientToggle}
            ambientPreset={ambientPreset}
            onAmbientPreset={applyAmbientPreset}
            ambientMaster={ambientMaster}
            onAmbientMaster={onAmbientMaster}
            ambientTracks={ambientTracks}
            onAmbientTrack={onAmbientTrack}
            showLabels={showLabels}
            onToggleLabels={() => setShowLabels((v) => !v)}
            labelsAvailable={activeSlug === 'r1as'}
            featureMap={featureMap}
            featureVisibility={featureVisibility}
            onToggleFeature={toggleFeature}
            onRemoveDoors={removeDoors}
            onRestoreDoors={restoreDoors}
            highlightDoors={highlightDoors}
            onToggleHighlightDoors={() => setHighlightDoors((v) => !v)}
            traversePlaying={traversePlaying}
            traverseStopLabel={traverseStopLabel}
            onPlayTraverse={playTraverse}
            onStopTraverse={stopTraverse}
            stats={stats}
            status={status}
            shotUrl={shotUrl}
            capturing={capturing}
            onCapture={capture}
            onPreset={goPreset}
            activePreset={activePreset}
            walkMode={walkMode}
            onToggleWalk={toggleWalk}
            annotateMode={annotateMode}
            onToggleAnnotate={toggleAnnotate}
            annotations={annotations}
            annotateKind={annotateKind}
            onAnnotateKind={setAnnotateKindLogged}
            annotateStationId={annotateStationId}
            onAnnotateStationId={setAnnotateStationLogged}
            annotateSelectedId={annotateSelectedId}
            onAnnotateSelect={setAnnotateSelectedId}
            onAnnotationDelete={onAnnotationDelete}
            onAnnotationClear={onAnnotationClear}
            onAnnotationExport={onAnnotationExport}
            onAnnotationNotes={onAnnotationNotes}
            annotateSource={annotateSource}
            authEmail={authEmail}
            stationIds={stationIds}
            annotationKinds={ANNOTATION_KINDS}
          />
        }
      >
        <div className="locate-viewer">
          <LabViewer
            key={`${activeUrl}#${loadGen}`}
            modelUrl={activeUrl}
            slug={activeSlug}
            layers={layers}
            showLabels={showLabels && activeSlug === 'r1as'}
            featureVisibility={featureVisibility}
            highlightDoors={highlightDoors}
            sectionCut={sectionCut}
            exteriorId={exteriorId}
            exteriorSkyId={exteriorSkyId}
            exteriorLayers={exteriorLayers}
            walkMode={walkMode}
            onWalkExitRequest={exitWalk}
            annotateMode={annotateMode}
            annotations={annotations}
            annotateKind={annotateKind}
            annotateStationId={annotateStationId}
            annotateSelectedId={annotateSelectedId}
            onAnnotationAdd={onAnnotationAdd}
            onAnnotateSelect={setAnnotateSelectedId}
            onStats={setStats}
            onFeatureMap={setFeatureMap}
            onBindApi={onBindApi}
          />
          <LabAnnotateConsole
            open={annotateMode}
            kind={annotateKind}
            stationId={annotateStationId}
            pinCount={annotations.length}
            logs={annotateLogs}
            walkMode={walkMode}
            source={annotateSource}
            authEmail={authEmail}
          />
        </div>
      </XlabsStageChrome>
    </div>
  );
}
